import demoInputs from './demoReviewInputs.json';
import demoPriority from './demoPriority.generated.json';

/**
 * Synthetic client book of a Spanish independent advisory firm.
 *
 * This is the dataset behind the public /demo route. It exists so a visitor
 * can see the review path — list, client card, report — without registering
 * and without handing over a single row of their own client data.
 *
 * Three rules were applied when writing it, and anything added later has to
 * keep them:
 *
 *   1. Everything here is synthetic and says so. No value comes from a real
 *      client, firm, product or market series, and the names are invented.
 *   2. No outcome is claimed. The fields are descriptions of what an extract
 *      contains — declared risk, concentration, drift, the date of the data —
 *      never a performance figure, a forecast, or a suitability verdict.
 *   3. Missing data stays missing. Clients with no source date or no
 *      knowledge score carry null, and the UI shows that as missing rather
 *      than as a zero. This is the single most important behaviour to show:
 *      it is what separates the product from a scoring toy.
 *
 * Narrative text is not stored here. Each row carries signal, reason and
 * next-step codes that src/i18n resolves, so the book is bilingual without a
 * second copy of the prose and a non-developer can review the wording in one
 * file per language.
 *
 * Urgency/freshness labels are generated offline by the authoritative engine
 * policy for the full synthetic book. Scores remain illustrative inputs,
 * rather than claims that the complete customer scoring service ran here.
 * The frozen reference date is displayed in the demo.
 */

export const DEMO_BOOK_SIZE = demoPriority.totals.clients;
export const DEMO_HIGH_TOP_PCT = demoPriority.policy.high_top_pct;
export const DEMO_MEDIUM_TOP_PCT = demoPriority.policy.medium_top_pct;
export const demoUrgencyPolicy = demoPriority.policy;
export const demoReferenceDate = demoPriority.referenceDate;

// Illustrative import metadata. Individual examples retain their own source
// dates (including missing dates), which determine their freshness below.
export const demoBookSource = {
    fileName: 'cartera-clientes-2026-10.xlsx',
    importedOn: '2026-10-16',
    observedOn: '2026-10-14',
    rows: DEMO_BOOK_SIZE,
    columns: 28,
    currency: 'EUR',
};

export const demoFirmProfile = {
    cityKey: 'demo.firm.city',
    advisers: 1,
    analysts: 1,
    clients: DEMO_BOOK_SIZE,
    // Assets under advice, in euro. A book descriptor from the extract, not a
    // result: it is the sum of the portfolio values that were uploaded.
    assetsEur: 42600000,
    medianClientEur: 186000,
};

export const demoBookTotals = {
    ...demoPriority.totals,
    // Illustrative coverage of the full synthetic extract, not customer usage.
    evidenceCoverage: 0.78,
};

// Distributions of the whole book. Each one sums to DEMO_BOOK_SIZE; the test
// in demoBookEs.test.js fails if an edit breaks that.
export const demoRiskDistribution = [
    { bucket: '1-3', clients: 41 },
    { bucket: '4-6', clients: 88 },
    { bucket: '7-8', clients: 39 },
    { bucket: '9-10', clients: 16 },
];

export const demoAgeDistribution = [
    { bucket: '25-39', clients: 28 },
    { bucket: '40-54', clients: 71 },
    { bucket: '55-69', clients: 62 },
    { bucket: '70+', clients: 23 },
];

export const demoCountryDistribution = [
    { country: 'ESP', clients: 158 },
    { country: 'PRT', clients: 9 },
    { country: 'FRA', clients: 7 },
    { country: 'DEU', clients: 4 },
    { country: 'MEX', clients: 3 },
    { country: 'ARG', clients: 3 },
];

export const demoAdvisers = [
    { id: 'ase-01', nameKey: 'demo.adviser.lead' },
    { id: 'ase-02', nameKey: 'demo.adviser.analyst' },
];

// Only the named examples are shown. The policy fixture was computed on all
// 184 synthetic inputs, including the unshown tail, before this display cut.
// Unactionable examples are a separate Verify group with no rank or score;
// the engine's underlying LOW label is retained as engineUrgency for audit.
const examplesById = new Map(demoInputs.clients.map((client) => [client.clientId, client]));
export const demoReviewQueue = demoPriority.visible.map((priority) => ({
    ...examplesById.get(priority.clientId),
    ...priority,
}));

export const findDemoClient = (clientId) =>
    demoReviewQueue.find((client) => client.clientId === clientId) || demoReviewQueue[0];

export const formatEur = (amount, language = 'es') => {
    const locale = language === 'es' ? 'es-ES' : 'en-IE';
    return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: 'EUR',
        maximumFractionDigits: 0,
    }).format(amount);
};

export const formatDate = (isoDate, language = 'es') => {
    if (!isoDate) return null;
    const locale = language === 'es' ? 'es-ES' : 'en-GB';
    return new Date(`${isoDate}T00:00:00Z`).toLocaleDateString(locale, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        timeZone: 'UTC',
    });
};
